import { SubjectsBadge } from "@/components/SubjectsBadge";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ProfileNudge } from "@/components/app/profile/ProfileNudge";
import { savedGradesToConfirm } from "@/lib/persona";
import { quickCheckPrefill } from "@/lib/quickCheckPrefill";
import { useState, useEffect, useRef } from "react";
import { Check, Bookmark } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ChanceLegend from "@/components/app/ChanceLegend";
import GapFixerModal from "@/components/app/GapFixerModal";
import { pushPendingShortlist } from "@/lib/pendingIntent";
import { toast } from "sonner";
import { readCache, writeCache, stableHash } from "@/lib/useCachedFetch";
import { useShortlistWriter } from "@/lib/useShortlistWriter";
import { saveApplicantProfile } from "@/lib/applicantProfile";

// Extracted view components (refactor Feb 2026). State + orchestration
// still lives in this page; each child is a pure view.
import GradesForm from "@/components/admission/GradesForm";
import CountryCard from "@/components/admission/CountryCard";
import WithinReachPanel from "@/components/admission/WithinReachPanel";
import ResultRow from "@/components/admission/ResultRow";
import ExcludedPanel from "@/components/admission/ExcludedPanel";
import UpsellToAdvanced from "@/components/admission/UpsellToAdvanced";
import CheapestCallout from "@/components/admission/CheapestCallout";
import RowErrorBoundary from "@/components/app/RowErrorBoundary";
import SubjectFilter from "@/components/app/SubjectFilter";
import AhaRibbon from "@/components/AhaRibbon";
import { compareSafest } from "@/lib/safestSort";
import { compareCompetitive } from "@/lib/competitiveSort";
import { CompetitiveReadingNote } from "@/components/admission/CompetitiveReadingNote";
import { subjectsKey, reopenPlan, countryStates } from "@/lib/subjectRefresh";
import { useCompareSelection, toggleCompare, compareKey, COMPARE_MAX } from "@/lib/compareSelection";
import { CompareTray } from "@/components/admission/CompareTray";
import { sameScoreIds, groupedDisplayOrder } from "@/components/admission/SameScoreTag";
import { SubjectChoicesPanel } from "@/components/admission/SubjectChoicesPanel";
import { restoreQuickCheckSubjects, subjectList } from "@/lib/subjectChoices";
import ApplyReadinessCard from "@/components/ApplyReadinessCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useJourney } from "@/context/JourneyContext";
import { groupByUniversity } from "@/lib/groupByUniversity";

// Two-tier sessionStorage cache prefixes for Quick Check. Both are keyed on
// a hash of the applicant payload so any profile edit auto-invalidates.
const QC_SUMMARY_PREFIX = "wua:qc-summary:v2:";
// v2 = per-row salary + acceptance fields added (2026-08-03)
const QC_DRILL_PREFIX   = "wua:qc-drill:v4:"; // v4: list-view rows (fields this page never reads dropped server-side)
const QC_CHEAPEST_PREFIX = "wua:qc-cheapest:v3:"; // bumped 2026-03-05 (per-region top-5 fetches now use limit=200)

export default function AdmissionCalculator() {
  const { user, login, loading: authLoading } = useAuth();
  const { updateProfile: journeyUpdateProfile, profile: journeyProfile, loaded: journeyLoaded, confirmPillar: journeyConfirmPillar, pillarProgress } = useJourney();

  const navigate = useNavigate();
  const { addToShortlist: writeShortlist, removeFromShortlist: dropShortlist } = useShortlistWriter();
  const [form, setForm] = useState({
    curriculum: "a_level",
    a_level: "A*AA",
    ib: 38,
    cbse: 92,
    american_diploma: 3.8,
    gpa: 3.7,
    atar: 90,     // Australian Tertiary Admission Rank (0.00–99.95)
    // New international curricula — routed through the cross-curriculum
    // translation layer in ai_advisor.classify().
    isc: 88,           // ISC / CISCE overall %
    ap_composite: 4.2, // Average AP exam score across ≥3 exams (1.0–5.0)
    abitur: 1.7,       // German Abitur — 1.0 best, 4.0 pass
    french_bac: 15,    // French Baccalauréat, /20
    hkdse: 27,         // HKDSE best-5 subject sum (0–35)
    suneung: 88,       // Suneung / CSAT top-3-section percentile average (0–100)
    sat: "",
    act: "",
    sat_act_status: "not_taken",   // "sat" | "act" | "not_taken"
    ielts: "",
    toefl: "",
    english_test_status: "not_taken", // "ielts" | "toefl" | "not_taken"
    // Per-subject grades — enforce subject-specific gates
    ib_maths: "", ib_english: "",
    a_level_maths: "", a_level_english: "",
    activities_strength: 3,
    intended_degrees: [],  // multi-select chips
    // Context flags — control which fields we ask for
    native_english: true,      // English is my school's language of instruction
    applying_to_us: false,     // I'm also applying to US universities
  });
  // "Prefilled from your saved profile" banner — set to true once the
  // mount effect has successfully hydrated grades from GET /profile.
  // Deliberately does NOT fire when the sessionStorage snapshot was the
  // source (that path never surfaces a banner on Quick Check, since the
  // user already saw one on the Advanced Wizard side).
  const [hydratedFromProfile, setHydratedFromProfile] = useState(false);
  // Snapshot of the server-persisted grade fields — used to compute the
  // "your profile still lists X" drift hint on the Save button. Set on
  // mount hydration, and refreshed after every successful save. Null =
  // no server profile at all (fresh account or anonymous), {} = server
  // has a profile but no grade fields set yet.
  const [serverGradeSnapshot, setServerGradeSnapshot] = useState(null);
  const [results, setResults] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [intendedShown, setIntendedShown] = useState("");
  const [loading, setLoading] = useState(false);
  // Progressive disclosure — Quick Check now fetches a country summary first
  // (tiny payload) and drills down into a country only when the user
  // expands it. `summary` holds the per-country band counts; `countryData`
  // caches the full-detail results by country so re-expanding a country
  // never hits the server twice.
  const [summary, setSummary] = useState(null);
  const [expanded, setExpanded] = useState({});      // { country: bool }
  const [countryData, setCountryData] = useState({}); // { country: {results, excluded, loading} }
  // Extra drill-down layer: within an expanded country, courses only show
  // once the user clicks a band chip (Likely / Target / Ambitious / Long-shot).
  // Prevents overwhelming the user with a huge list on country expansion.
  const [openBands, setOpenBands] = useState({});    // { country: { bucket: bool } }
  // Drill-down guards: generation bumps on every summary reset so stale country responses are dropped.
  const drillGenRef = useRef(0);
  const drillInflightRef = useRef(new Set());
  const revealPendingRef = useRef(false);
  const summaryGenRef = useRef(0);
  const reopenRef = useRef(null);
  // Client-side pagination for the results list. Some subjects (UK
  // Maths → 800+ target matches) freeze the frontend when rendered
  // all-at-once — we render 25 rows by default and let the user
  // ask for more in 25-row batches.
  const PAGE_SIZE = 25;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // "Your buckets" sort mode — client-side, no re-fetch. The backend
  // ships every field this dropdown needs (cost, chance gap_score,
  // salary, acceptance, name) in one payload so switching orders is
  // instant.
  const [bucketsSort, setBucketsSort] = useState("competitive");
  // Subject filter — pill dropdown shown on the "Your buckets" header
  // whenever the applicant picked more than one intended subject.
  // Empty means "All subjects"; otherwise narrows the flat results
  // and excluded arrays via each row's backend-attached
  // `matched_intended` list. Purely client-side — no re-fetch.
  const [subjectFilter, setSubjectFilter] = useState("");
  // City filter — driven by the cities the student confirmed on the
  // PLACE stage of the journey. Kept as a Set of city_slugs so the
  // student can drop a city per-chip without losing the others.
  // Empty Set = "no filter" (Set with entries = only these cities).
  const [cityFilter, setCityFilter] = useState(() => new Set());
  // Initial hydrate: seed the filter from cities_confirmed the first
  // time the journey profile is loaded. Runs once — after that the
  // student is in control of the filter chips.
  const citySeededOnce = useRef(false);
  useEffect(() => {
    if (citySeededOnce.current) return;
    if (!journeyLoaded) return;
    const confirmed = (journeyProfile?.where?.cities_confirmed || [])
      .map((c) => (typeof c === "string" ? c : (c?.slug || c?.city_slug)))
      .filter(Boolean);
    if (!confirmed.length) return;
    citySeededOnce.current = true;
    setCityFilter(new Set(confirmed));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyLoaded, journeyProfile?.where?.cities_confirmed]);
  // Confirmed-city name lookup for the chip strip labels.
  const confirmedCityLabels = (journeyProfile?.where?.cities_confirmed || [])
    .map((c) => typeof c === "string"
      ? { slug: c, name: c }
      : { slug: c?.slug || c?.city_slug, name: c?.name || c?.city || c?.slug })
    .filter((c) => c.slug);
  // Group-by-university toggle — collapses each uni into a single
  // flagship row + a "N variations ▾" chip that expands sibling
  // courses on click. Default ON when the family has picked an
  // intended subject (search-heavy flows), OFF for open browsing
  // where the flat list is what a user expects.
  const [groupByUni, setGroupByUni] = useState(true);
  const [expandedUniGroups, setExpandedUniGroups] = useState(() => new Set());
  // Ref on the "Your buckets" section so we can smooth-scroll the
  // family to the courses the moment they click a band chip.
  const resultsRef = useRef(null);
  const chancesRef = useRef(null);
  // First tier click also auto-expands the top result's Explain so
  // the family instantly sees one worked example instead of hunting
  // for a button.
  const autoExplainedRef = useRef(false);
  // Inline AI narratives — one per university.
  const [aiByUni, setAiByUni] = useState({});      // { [university_id]: narrativeResponse }
  const [aiLoadingId, setAiLoadingId] = useState(null);

  // "Universities within reach" — escape-hatch for the discouraged
  // applicant whose every match came back as ambitious or long-shot.
  // Lazy-loaded via `POST /admission/chance/within-reach` when the
  // student clicks the CTA (never on initial submit).
  const [withinReach, setWithinReach] = useState(null); // {items, total_considered}
  const [withinReachLoading, setWithinReachLoading] = useState(false);

  // "Cheapest 5 in your buckets" — top of results strip driven by the
  // /admission/chance/cheapest endpoint. Auto-fires alongside the summary
  // so the shock-value comparison (Germany €0 vs USA $80k) lands before
  // the applicant starts drilling into countries. Result set is small
  // (<=5 rows) so a single stateful blob is enough.
  const [cheapest, setCheapest] = useState(null);   // { items, total_considered, intended_degree }
  const [cheapestLoading, setCheapestLoading] = useState(false);

  // Save-search dialog state — replaces the ugly native window.prompt().
  // `saveDialogOpen` toggles the modal; `saveDialogName` is the controlled
  // input, `savingSearch` disables the primary button while POSTing.
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveDialogName, setSaveDialogName] = useState("");
  const [savingSearch, setSavingSearch] = useState(false);

  // Fields the student actually entered/confirmed. Form defaults (A Levels, A*AA, IB 38…) are
  // starting values only and must never be mirrored or saved as the student's own answers.
  const explicitRef = useRef(new Set());
  const [explicitGrades, setExplicitGrades] = useState(() => new Set());
  const markExplicit = (keys) => {
    keys.forEach((k) => explicitRef.current.add(k));
    setExplicitGrades(new Set(explicitRef.current));
  };
  // touchedRef = edited on this page; late hydration never overwrites these.
  const touchedRef = useRef(new Set());
  const serverExplicitRef = useRef(new Set());
  const [prefilledConfirmed, setPrefilledConfirmed] = useState(() => new Set());
  const update = (k, v) => {
    touchedRef.current.add(k);
    markExplicit([k]);
    setPrefilledConfirmed((s) => { if (!s.has(k)) return s; const n = new Set(s); n.delete(k); return n; });
    setForm((f) => ({ ...f, [k]: v }));
  };
  const mirrorGradesToJourney = (f = form) => {
    const ex = explicitRef.current;
    const gk = f.curriculum;
    const grades = { intended_degrees: (f.intended_degrees || []).filter(Boolean) };
    // Stamp only the student's own edits, so a later Pick confirmation can still take over.
    const editedAt = touchedRef.current.has("intended_degrees") ? new Date().toISOString() : journeyProfile?.how?.grades?.intended_degrees_at;
    if (editedAt) grades.intended_degrees_at = editedAt;
    if (ex.has("curriculum") || ex.has(gk)) {
      const val = f[gk];
      const predOk = ex.has(gk) && val !== "" && val !== null && val !== undefined;
      const stored = journeyProfile?.how?.grades || {};
      grades.curriculum = gk;
      grades.source = "quick_check";
      grades.confirmed = { curriculum: true, predicted: predOk };
      if (predOk) grades.predicted = String(val);
      else if (!(stored.confirmed?.curriculum && stored.curriculum === gk)) grades.predicted = null;
    }
    journeyUpdateProfile({ how: { grades } });
  };

  // Reverse-sync: signed-in users with a saved Advanced-profile land on
  // Quick Check with their curriculum + grades already filled. Fires
  // exactly once per mount, only when the user is authenticated. If
  // the profile lookup 404s or the returned object lacks a curriculum
  // we silently fall through to the built-in defaults.
  //
  // Precedence:
  //   1. Server profile (this effect)           — canonical
  //   2. sessionStorage snapshot from a prior Quick Check run — session-only
  //   3. Defaults set in `useState` above
  //
  // We deliberately only overwrite the SHARED grade fields (see
  // `applicantProfile.js`), never the Quick-Check-only UI toggles
  // (native_english, applying_to_us, sat_act_status, english_test_status).
  const profileHydrateOnce = useRef(false);
  const [profileFetched, setProfileFetched] = useState(false);
  // Once journey (and, signed in, the server profile) has loaded: prefill this student's own
  // confirmed grades, then any "Edit in Quick Check" draft (?draft=saved) as unconfirmed values.
  const [searchParams] = useSearchParams();
  const prefillOnce = useRef(false);
  const [draftFromSaved, setDraftFromSaved] = useState(false);
  useEffect(() => {
    if (prefillOnce.current || !journeyLoaded || (user && !profileFetched)) return;
    prefillOnce.current = true;
    const draftItem = searchParams.get("draft") === "saved" ? savedGradesToConfirm(journeyProfile) : null;
    const { changes, confirmedFields, draftFields } = quickCheckPrefill({
      form, journeyProfile, draftItem, touched: touchedRef.current, serverExplicit: serverExplicitRef.current,
    });
    if (!Object.keys(changes).length) return;
    setForm((f) => {
      const next = { ...f };
      for (const [k, v] of Object.entries(changes)) if (!touchedRef.current.has(k)) next[k] = v;
      return next;
    });
    markExplicit(confirmedFields);
    setPrefilledConfirmed((s) => new Set([...s, ...confirmedFields]));
    if (draftFields.length) setDraftFromSaved(true);
  }, [searchParams, journeyLoaded, user, profileFetched]); // eslint-disable-line react-hooks/exhaustive-deps
  // Restore the full ordered subject list once (never over an in-page edit).
  const profileSubjectsRef = useRef([]);
  const subjectsRestoreOnce = useRef(false);
  useEffect(() => {
    if (subjectsRestoreOnce.current || authLoading || !journeyLoaded || (user && !profileFetched)) return;
    subjectsRestoreOnce.current = true;
    const list = restoreQuickCheckSubjects({ journeyProfile, profileSubjects: profileSubjectsRef.current });
    if (!list.length) return;
    setForm((f) => (touchedRef.current.has("intended_degrees") ? f : { ...f, intended_degrees: list }));
  }, [authLoading, journeyLoaded, user, profileFetched]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (profileHydrateOnce.current) return;
    if (!user) return;
    profileHydrateOnce.current = true;
    (async () => {
      try {
        const { data } = await api.get("/profile");
        // Declared not-taken tests (Profile page) travel into every Quick Check payload.
        if (Array.isArray(data?.tests_not_taking) && data.tests_not_taking.length) {
          setForm((f) => ({ ...f, tests_not_taking: data.tests_not_taking }));
        }
        if (!data || !data.curriculum) return;
        const SHARED = [
          "curriculum",
          "a_level", "ib", "cbse", "american_diploma", "gpa", "atar", "isc",
          "ap_composite", "abitur", "french_bac", "hkdse", "suneung",
          "a_level_maths", "a_level_english", "ib_maths", "ib_english",
          "sat", "act", "ielts", "toefl",
          "activities_strength",
        ];
        // Snapshot what's canonically on the server so we can flag
        // drift on the Save button. Kept as a raw object so the diff
        // is straight equality.
        const snap = {};
        for (const k of SHARED) {
          if (data[k] !== undefined && data[k] !== null && data[k] !== "") {
            snap[k] = data[k];
          }
        }
        const serverSubjects = subjectList(data.intended_degrees?.length ? data.intended_degrees : data.intended_degree);
        if (serverSubjects.length) snap.intended_degrees = serverSubjects;
        profileSubjectsRef.current = serverSubjects;
        setServerGradeSnapshot(snap);
        const prov = data.field_provenance || {};
        const serverExplicit = [...SHARED, "intended_degree"].filter((k) => prov[k]?.explicitly_answered === true)
          .map((k) => (k === "intended_degree" ? "intended_degrees" : k))
          .filter((k) => !touchedRef.current.has(k));
        serverExplicit.forEach((k) => serverExplicitRef.current.add(k));
        markExplicit(serverExplicit);
        setPrefilledConfirmed((s) => new Set([...s, ...serverExplicit]));
        // Unconfirmed server values stay working values for matching only (labelled unconfirmed).
        setForm((f) => {
          const next = { ...f };
          for (const k of SHARED) {
            if (touchedRef.current.has(k)) continue;
            if (data[k] !== undefined && data[k] !== null && data[k] !== "") {
              next[k] = data[k];
            }
          }
          return next;
        });
        setHydratedFromProfile(true);
      } catch (_) {
        // 404 (no profile yet) is expected for fresh accounts — silently ignore.
      } finally {
        setProfileFetched(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Auto-classify (debounced) whenever the form changes. Fires the SUMMARY
  // endpoint only — drill-down happens on user expand. The old fire-full-
  // bulk-on-every-keystroke pattern shipped an 8+ MB payload per debounce
  // cycle once directory rows landed; this now ships ~1 KB per cycle.
  //
  // Gate: only auto-fire once the applicant has picked a curriculum AND at
  // least one intended-degree chip. Below the gate the form is decorative
  // and the summary would be misleading anyway.
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    const hasCurriculum = !!form.curriculum;
    const hasIntent = (form.intended_degrees || []).filter(Boolean).length > 0;
    if (!hasCurriculum || !hasIntent) return;
    const t = setTimeout(() => { void submit(); }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.curriculum, form.a_level, form.ib, form.cbse, form.american_diploma, form.gpa,
    form.atar, form.isc, form.ap_composite, form.abitur, form.french_bac, form.hkdse, form.suneung,
    form.sat, form.act, form.ielts, form.toefl,
    form.ib_maths, form.ib_english, form.a_level_maths, form.a_level_english,
    form.activities_strength, form.intended_degrees.join("|"),
    form.native_english, form.applying_to_us,
    form.sat_act_status, form.english_test_status,
    // City filter changes must retrigger the summary so the country
    // counts and cheapest strip agree with what the drill-down shows.
    Array.from(cityFilter).sort().join("|"),
  ]);

  // Adding/removing a subject: drop the old subjects' courses at once and mark the open
  // countries as loading; submit() (debounced above) reloads them for the new list.
  const subjKey = subjectsKey(form.intended_degrees);
  const prevSubjKeyRef = useRef(subjKey);
  useEffect(() => {
    if (prevSubjKeyRef.current === subjKey) return;
    prevSubjKeyRef.current = subjKey;
    const plan = reopenPlan(expanded, openBands);
    drillGenRef.current += 1;
    summaryGenRef.current += 1;
    setResults([]);
    setExcluded([]);
    setAiByUni({});
    setAiLoadingId(null);
    setWithinReach(null);
    setCheapest(null);
    setVisibleCount(PAGE_SIZE);
    setExpandedUniGroups(new Set());
    if (subjectFilter && !subjKey.split("|").includes(subjectFilter)) setSubjectFilter("");
    if (!subjKey || !form.curriculum) {
      reopenRef.current = null;
      setExpanded({});
      setOpenBands({});
      setCountryData({});
      setLoading(false);
      return;
    }
    reopenRef.current = plan.countries.length ? plan : null;
    setCountryData(countryStates(plan.countries, { loading: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjKey]);

  // Shared payload builder — used by both summary + drill-down calls.
  const buildPayload = () => {
    const scrubbed = { ...form };
    if (scrubbed.sat_act_status !== "sat") scrubbed.sat = "";
    if (scrubbed.sat_act_status !== "act") scrubbed.act = "";
    if (scrubbed.english_test_status !== "ielts") scrubbed.ielts = "";
    if (scrubbed.english_test_status !== "toefl") scrubbed.toefl = "";
    const chips = (scrubbed.intended_degrees || []).filter(Boolean);
    delete scrubbed.intended_degrees;
    if (chips.length === 1) scrubbed.intended_degree = chips[0];
    else if (chips.length > 1) scrubbed.intended_degree = chips;
    if (scrubbed.native_english) scrubbed.english_waived = true;
    delete scrubbed.native_english;
    // City filter (from PLACE pillar) — sent so the backend country
    // summary, cheapest, drill-down and within-reach all agree.
    const citySlugs = Array.from(cityFilter || []);
    if (citySlugs.length) scrubbed.preferred_city_slugs = citySlugs;
    return Object.fromEntries(
      Object.entries(scrubbed).filter(([k, v]) =>
        !["sat_act_status", "english_test_status"].includes(k) &&
        v !== "" && v !== null && v !== undefined &&
        !(Array.isArray(v) && v.length === 0)
      )
    );
  };

  // Save-to-profile — promotes the current Quick Check grades into the
  // signed-in user's canonical Advanced profile via POST /api/profile.
  // Kept as a partial patch (curriculum + grade fields only) so we
  // don't clobber unrelated profile bits like preferred countries,
  // budget, or fit weights that live only in the Advanced Wizard.
  const [savingProfileState, setSavingProfileState] = useState("idle");
  const saveGradesToProfile = async ({ silent = false } = {}) => {
    if (!user) return;
    if (!silent) setSavingProfileState("saving");
    try {
      const patch = {
        curriculum: form.curriculum,
        a_level: form.a_level || null,
        ib: form.ib || null,
        cbse: form.cbse || null,
        american_diploma: form.american_diploma || null,
        gpa: form.gpa || null,
        atar: form.atar || null,
        isc: form.isc || null,
        ap_composite: form.ap_composite || null,
        abitur: form.abitur || null,
        french_bac: form.french_bac || null,
        hkdse: form.hkdse || null,
        suneung: form.suneung || null,
        a_level_maths: form.a_level_maths || null,
        a_level_english: form.a_level_english || null,
        ib_maths: form.ib_maths || null,
        ib_english: form.ib_english || null,
        sat: form.sat || null,
        act: form.act || null,
        ielts: form.ielts || null,
        toefl: form.toefl || null,
        activities_strength: form.activities_strength,
      };
      // Same wire format as the Advanced Wizard: one subject = string, several = the full ordered list.
      const chips = (form.intended_degrees || []).filter(Boolean);
      if (chips.length) patch.intended_degree = chips.length === 1 ? chips[0] : chips;
      patch.explicit_fields = [...explicitRef.current]
        .map((k) => (k === "intended_degrees" ? "intended_degree" : k))
        .filter((k) => k in patch);
      patch.source_surface = "quick_check";
      await api.post("/profile", patch);
      // Snapshot now matches whatever we just sent — update our local
      // copy so the drift hint disappears immediately.
      setServerGradeSnapshot((prev) => ({
        ...(prev || {}),
        ...Object.fromEntries(
          Object.entries(patch).filter(([, v]) => v !== null && v !== undefined && v !== ""),
        ),
        intended_degrees: form.intended_degrees && form.intended_degrees.length
          ? form.intended_degrees
          : (prev?.intended_degrees || undefined),
      }));
      setSavingProfileState("saved");
      if (!silent) toast.success("Saved to your profile.");
      // Reset the button label after a beat so subsequent edits can
      // re-save without a page reload.
      setTimeout(() => setSavingProfileState("idle"), 3500);
    } catch (err) {
      setSavingProfileState("idle");
      if (!silent) toast.error(err?.response?.data?.detail || "Could not save to profile.");
    }
  };


  // Reopen what the user had open after a subject change, now loading the new subjects' courses.
  const reopenAfterSubjectChange = () => {
    const plan = reopenRef.current;
    reopenRef.current = null;
    if (!plan) return;
    setExpanded(plan.expanded);
    setOpenBands(plan.openBands);
    plan.countries.forEach((c) => { void loadCountry(c); });
  };

  const submit = async () => {
    const gen = ++summaryGenRef.current;
    setLoading(true);
    // Sync applicant snapshot to sessionStorage so that Advanced Wizard
    // can hydrate from the same grades without asking the user again.
    // Done at the top of submit so even a cached-summary early-return
    // still leaves the sync in place.
    saveApplicantProfile(form, [...explicitRef.current]);
    // Silent server-side persistence for signed-in users. This is what
    // makes "I told the system I want to study Economics" survive across
    // tabs, sessions, and devices — sessionStorage alone dies with the
    // tab. Fire-and-forget so a slow /profile PATCH never blocks the
    // Quick Check result from rendering. Uses PATCH semantics on the
    // backend so unrelated fields (budget, prefs, fit weights) survive.
    if (user) {
      saveGradesToProfile({ silent: true }).catch(() => {});
    }
    try {
      const payload = buildPayload();
      const summaryKey = `${QC_SUMMARY_PREFIX}${stableHash(payload)}`;
      const cheapestKey = `${QC_CHEAPEST_PREFIX}${stableHash(payload)}`;
      // Hydrate instantly from cache if the exact profile has been submitted
      // before this session — Quick Check hits this hot path on every
      // debounced auto-classify, so a cache hit saves a full 200-500ms round-trip.
      const cached = readCache(summaryKey);
      const cachedCheapest = readCache(cheapestKey);
      if (cached) {
        setSummary(cached);
        setIntendedShown(cached.intended_degree || "");
        setExpanded({});
        drillGenRef.current += 1;
        setCountryData({});
        setOpenBands({});
        setResults([]);
        setExcluded([]);
        setAiByUni({});
        setAiLoadingId(null);
        setWithinReach(null);
        setVisibleCount(PAGE_SIZE);
        autoExplainedRef.current = false;
        setCheapest(cachedCheapest || null);
        setCheapestLoading(false);
        setLoading(false);
        reopenAfterSubjectChange();
        // Even on summary cache-hit, refetch cheapest silently if the
        // browser didn't cache it (e.g. new tab, cleared session).
        if (!cachedCheapest) {
          fetchCheapest(payload, cheapestKey, gen);
        }
        return;
      }
      const { data } = await api.post("/admission/chance/summary", payload);
      if (gen !== summaryGenRef.current) return; // a newer edit superseded this request
      setSummary(data);
      setIntendedShown(data.intended_degree || "");
      writeCache(summaryKey, data);
      // Mirror only student-entered/confirmed grades into the journey (never completion).
      try { mirrorGradesToJourney(); } catch { /* best-effort */ }
      // Clear stale expansion state — profile changed so any per-country
      // cache is invalid. Expand nothing by default; user picks.
      setExpanded({});
      drillGenRef.current += 1;
      setCountryData({});
      setOpenBands({});
      setResults([]);
      setExcluded([]);
      setAiByUni({});
      setAiLoadingId(null);
      setWithinReach(null);
      setVisibleCount(PAGE_SIZE);
      autoExplainedRef.current = false;
      reopenAfterSubjectChange();
      // Kick off cheapest fetch in the background — don't block the
      // summary render. Cheapest is decorative until data arrives.
      if (cachedCheapest) {
        setCheapest(cachedCheapest);
        setCheapestLoading(false);
      } else {
        fetchCheapest(payload, cheapestKey, gen);
      }
    } catch (e) {
      if (gen !== summaryGenRef.current) return;
      const plan = reopenRef.current;
      reopenRef.current = null;
      if (plan) {
        // Old courses are already cleared; leave each open country on its Retry state.
        setExpanded(plan.expanded);
        setOpenBands(plan.openBands);
        setCountryData(countryStates(plan.countries, { loading: false, error: true }));
      }
      toast.error("Could not compute chances");
    } finally {
      if (gen === summaryGenRef.current) setLoading(false);
    }
  };

  // Standalone fetcher so submit() can early-return on summary-cache hits
  // and still keep the cheapest strip in sync.
  const fetchCheapest = async (payload, cacheKey, gen) => {
    setCheapestLoading(true);
    setCheapest(null);
    try {
      // Fetch a large pool so the frontend can slice cheapest-N *per
      // region* client-side (UK / Europe / N. America / Asia / …). With
      // only limit=5, filtering by region often yielded 1-2 items.
      const { data } = await api.post("/admission/chance/cheapest?limit=200", payload);
      writeCache(cacheKey, data);
      if (gen !== summaryGenRef.current) return;
      setCheapest(data);
    } catch {
      // Silently swallow — the strip is decorative. Leaving `cheapest`
      // null hides it entirely rather than showing a broken state.
      if (gen === summaryGenRef.current) setCheapest(null);
    } finally {
      if (gen === summaryGenRef.current) setCheapestLoading(false);
    }
  };

  // "Within reach" fetch — only fired on user click, never on submit,
  // because the payload isn't cheap and most profiles don't need it.
  const loadWithinReach = async () => {
    if (withinReach || withinReachLoading) return;
    setWithinReachLoading(true);
    try {
      const payload = buildPayload();
      const { data } = await api.post("/admission/chance/within-reach?limit=12", payload);
      setWithinReach(data);
    } catch {
      toast.error("Couldn't fetch closest matches. Try again in a moment.");
    } finally {
      setWithinReachLoading(false);
    }
  };

  // Derived flat results: union of every currently-expanded country's
  // drill-down, further filtered by which bands the user has clicked open
  // inside that country. Country expansion alone does NOT reveal courses —
  // the user must click a band chip (Likely/Target/Ambitious/Long-shot).
  useEffect(() => {
    const flat = [];
    const flatExcluded = [];
    // Subject filter helper — subject_not_offered exclusions carry
    // `matched_intended: []` and drop out under any specific chip.
    const matchesSubject = (r) =>
      !subjectFilter || (r?.matched_intended || []).includes(subjectFilter);
    // City filter helper — empty Set means "no city constraint".
    // Otherwise the row's city_slug (fallback: lower-cased city name)
    // must be in the Set of confirmed cities.
    const matchesCity = (r) => {
      if (cityFilter.size === 0) return true;
      const slug = r?.city_slug
        || (r?.city ? String(r.city).toLowerCase().replace(/\s+/g, "-") : null);
      return !!slug && cityFilter.has(slug);
    };
    Object.entries(countryData).forEach(([country, d]) => {
      if (!expanded[country] || !d) return;
      const bands = openBands[country] || {};
      const anyOpen = Object.values(bands).some(Boolean);
      if (!anyOpen) return;
      (d.results || []).forEach((r) => {
        const bucket = r?.classification?.bucket;
        if (bucket && bands[bucket] && matchesSubject(r) && matchesCity(r)) flat.push(r);
      });
      (d.excluded || []).forEach((e) => {
        if (matchesSubject(e) && matchesCity(e)) flatExcluded.push(e);
      });
    });
    setResults(flat);
    setExcluded(flatExcluded);
  }, [expanded, countryData, openBands, subjectFilter, cityFilter]);
  useEffect(() => {
    if (!revealPendingRef.current || !results.length) return;
    revealPendingRef.current = false;
    requestAnimationFrame(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, [results]);

  // First-band-click delight: automatically open the "Why this fit?"
  // panel on the top result so the family sees a worked example
  // without hunting for the button. Fires exactly once per session
  // — subsequent tier clicks don't retrigger. Uses a small delay so
  // the smooth-scroll finishes AND the derived `results` array has
  // repopulated (state commits are batched).
  // First-band-click delight: automatically open the "Why this fit?"
  // panel on the *cheapest* result (which is the visually first row
  // once we sort by cost) so the family sees a worked example
  // without hunting for the button. Fires exactly once per session.
  useEffect(() => {
    if (autoExplainedRef.current) return;
    if (!results.length) return;
    // Mirror the sort used at render time so we auto-explain the
    // row the family actually sees at the top of the list.
    const sortedTop = [...results].sort((a, b) => {
      const ac = a.true_cost || {};
      const bc = b.true_cost || {};
      const sum = (c) => (c.tuition_usd == null || c.complete === false ? Infinity : c.tuition_usd + (c.fees_usd||0) + (c.accommodation_usd||0) + (c.food_usd||0));
      return sum(ac) - sum(bc);
    })[0];
    if (!sortedTop) return;
    const rowId = sortedTop.degree_id || sortedTop.university_id;
    if (aiByUni[rowId]) return;
    autoExplainedRef.current = true;
    const t = setTimeout(() => {
      getNarrative(sortedTop.university_id, rowId, sortedTop.degree_id);
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);


  // then serves it from cache on subsequent toggles.
  const expandCountry = async (country) => {
    setExpanded((m) => {
      const next = { ...m, [country]: !m[country] };
      // When collapsing, also clear open bands for this country so
      // re-expanding doesn't show stale course selections.
      if (m[country]) {
        setOpenBands((ob) => {
          const { [country]: _drop, ...rest } = ob;
          return rest;
        });
      }
      return next;
    });
    if (countryData[country] && !countryData[country].error) return; // loaded or in flight
    void loadCountry(country);
  };

  const loadCountry = async (country) => {
    const gen = drillGenRef.current;
    const flightKey = `${gen}:${country}`;
    if (drillInflightRef.current.has(flightKey)) return;
    const payload = buildPayload();
    const drillKey = `${QC_DRILL_PREFIX}${stableHash(payload)}:${country}`;
    // Hydrate the country's drill-down from cache instantly on revisit,
    // then reveal the band chips without a spinner. Any profile edit
    // produces a new payload hash and thus a new key.
    const cached = readCache(drillKey);
    if (cached) {
      setCountryData((m) => ({ ...m, [country]: { loading: false, ...cached } }));
      return;
    }
    drillInflightRef.current.add(flightKey);
    setCountryData((m) => ({ ...m, [country]: { loading: true, results: [], excluded: [] } }));
    try {
      const { data } = await api.post(
        `/admission/chance/bulk-degrees?country=${encodeURIComponent(country)}&view=list`,
        payload,
      );
      const shaped = { results: data.results || [], excluded: data.excluded || [] };
      writeCache(drillKey, shaped);
      if (gen !== drillGenRef.current) return; // profile changed while loading
      setCountryData((m) => ({ ...m, [country]: { loading: false, ...shaped } }));

      // Fire-and-forget log for future empirical calibration (Path B).
      // Only sends the anonymised gap+bucket+probability signature —
      // no PII. Failure is silently ignored; instrumentation must
      // never block the user's Quick-Check flow.
      try {
        // A compact key that summarises the applicant's academic
        // signature. Only the curriculum + its own grade value is
        // captured — no name, email, or profile prose.
        const gradeSig = (() => {
          const c = payload.curriculum;
          const v = payload[c];
          return v != null && v !== "" ? `${c}:${v}` : c;
        })();
        // One batched request per fresh country load (same rows/fields as the former per-row posts).
        const rows = (data.results || []).slice(0, 40).filter((r) => r.classification).map((r) => ({
          curriculum: payload.curriculum,
          grade_signature: gradeSig,
          subject: payload.intended_degree || null,
          country,
          university_id: r.university_id,
          degree_id: r.degree_id || null,
          bucket: r.classification.bucket,
          gap_score: r.classification.gap_score,
          admit_probability: r.classification.admit_probability?.value ?? null,
        }));
        if (rows.length) api.post("/admission/chance/log-submissions", { rows }).catch(() => { /* intentionally silent */ });
      } catch { /* intentionally silent */ }
    } catch {
      if (gen !== drillGenRef.current) return;
      setCountryData((m) => ({ ...m, [country]: { loading: false, error: true, results: [], excluded: [] } }));
      toast.error(`Could not load ${country} courses`);
    } finally {
      drillInflightRef.current.delete(flightKey);
    }
  };

  const toggleBand = (country, bucket) => {
    let willBeOpen = false;
    setOpenBands((m) => {
      const c = m[country] || {};
      const next = { ...m, [country]: { ...c, [bucket]: !c[bucket] } };
      // Track whether the tap opens (as opposed to closes) a band so
      // we can smooth-scroll only on reveal — never on collapse.
      willBeOpen = !c[bucket];
      return next;
    });
    // Reset pagination whenever the visible band-set changes; the
    // family always sees the first page of the newly-revealed slice.
    setVisibleCount(PAGE_SIZE);
    // Still fetching: stay by the band loader, reveal the results once the real rows land.
    if (willBeOpen && countryData[country]?.loading) { revealPendingRef.current = true; return; }
    if (willBeOpen) {
      // Two rAFs — one for the openBands state commit, one for the
      // useEffect that rebuilds `results`. Scroll then lands on a
      // section that's actually populated.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          resultsRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        });
      });
    }
  };

  const getNarrative = async (uni_id, rowKey, degree_id = null) => {
    // rowKey defaults to uni_id for backward compat; degree rows pass degree_id.
    const key = rowKey || uni_id;
    if (aiByUni[key]) {
      setAiByUni((m) => {
        const { [key]: _drop, ...rest } = m;
        return rest;
      });
      return;
    }
    setAiLoadingId(key);
    try {
      const scrubbed = { ...form };
      if (scrubbed.sat_act_status !== "sat") scrubbed.sat = "";
      if (scrubbed.sat_act_status !== "act") scrubbed.act = "";
      if (scrubbed.english_test_status !== "ielts") scrubbed.ielts = "";
      if (scrubbed.english_test_status !== "toefl") scrubbed.toefl = "";
      if (scrubbed.native_english) scrubbed.english_waived = true;
      delete scrubbed.native_english;
      const payload = Object.fromEntries(
        Object.entries(scrubbed).filter(([k, v]) =>
          !["sat_act_status", "english_test_status"].includes(k) &&
          v !== "" && v !== null && v !== undefined
        )
      );
      const body = { ...payload, university_id: uni_id };
      // Scope the narrative to the SPECIFIC degree so the LLM and breakdown
      // score against the course's real entry requirements, not the generic
      // university baseline.
      if (degree_id) body.course_slug = degree_id;
      const { data } = await api.post("/admission/chance", body);
      setAiByUni((m) => ({ ...m, [key]: data }));
    } catch (err) {
      console.error("[getNarrative] failed:", err);
      toast.error("Could not fetch AI explanation");
    }
    finally { setAiLoadingId(null); }
  };

  const [shortlistedIds, setShortlistedIds] = useState(new Set());
  // Unique-university tally (Set of university_id) — used to power
  // the "Apply readiness" progress card. Two degrees at the same
  // university count as one item toward the 3-uni threshold that
  // unlocks the APPLY pillar on `/compare`.
  const [shortlistUniIds, setShortlistUniIds] = useState(new Set());
  // Gap-fixer modal state — { gapKey, gapValue, degreeId, degreeName, universityName }
  const [gapFixer, setGapFixer] = useState(null);

  // Pre-populate the "already saved" state when the user is signed in, so the
  // Add-to-shortlist button on any narrative shows "Shortlisted" for prior selections.
  // Keyed by degree_id when present (each degree is its own product) and
  // falling back to university_id for legacy uni-only rows.
  useEffect(() => {
    if (!user) {
      setShortlistedIds(new Set());
      setShortlistUniIds(new Set());
      return;
    }
    const loadShortlist = async () => {
      try {
        const { data } = await api.get("/shortlist");
        const items = data.items || [];
        setShortlistedIds(new Set(items.map((i) => i.degree_id || i.university_id)));
        setShortlistUniIds(new Set(items.map((i) => i.university_id).filter(Boolean)));
      } catch {}
    };
    void loadShortlist();
    // Reflect shortlist changes made in another tab (Advanced Search, /shortlist).
    const onFocus = () => { if (document.visibilityState === "visible") void loadShortlist(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => { window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); };
  }, [user]);

  const addToShortlist = async (uni_id, uni_name, degree_id = null, degree_name = null) => {
    const key = degree_id || uni_id;
    const displayName = degree_name
      ? `${uni_name} — ${degree_name}`
      : uni_name;
    if (!user) {
      pushPendingShortlist(uni_id, uni_name, degree_id);
      toast(`Sign in to save ${displayName} — we'll add it for you automatically.`);
      // Give the user a moment to see the toast before OAuth redirect.
      setTimeout(login, 700);
      return;
    }
    try {
      const payload = { university_id: uni_id };
      if (degree_id) payload.degree_id = degree_id;
      await writeShortlist(payload);
      setShortlistedIds((s) => new Set([...s, key]));
      setShortlistUniIds((s) => new Set([...s, uni_id]));
      toast.success(`${displayName} added to your shortlist`);
      // Shortlisting from the Prove page IS the explicit action that
      // finishes this stage — the student has seen their chances and
      // committed to at least one course. Auto-confirm the PROVE
      // pillar so they don't have to hunt for a separate "confirm"
      // button, and so the tube-map advances naturally when they
      // move on to the shortlist page. Idempotent — silently a no-op
      // if Prove was already confirmed.
      if (!pillarProgress?.prove?.completed) {
        const chips = (form.intended_degrees || []).filter(Boolean);
        const confirmed = chips.length > 0
          ? chips.map((s) => ({ slug: s, label: s }))
          : [{ slug: displayName, label: displayName }];
        journeyConfirmPillar?.("prove", confirmed).catch(() => {});
      }
    } catch { toast.error("Could not add to shortlist"); }
  };

  // Toggle-off counterpart — lets the student click a "Shortlisted"
  // pill to remove the degree without leaving the page. Optimistically
  // removes from both Sets and rolls back on network failure so the
  // pill never gets stuck in a half-state.
  const removeFromShortlistLocal = async (uni_id, uni_name, degree_id = null, degree_name = null) => {
    if (!user) return;
    const key = degree_id || uni_id;
    const displayName = degree_name ? `${uni_name} — ${degree_name}` : uni_name;
    // Snapshot for rollback
    const hadKey = shortlistedIds.has(key);
    setShortlistedIds((s) => { const n = new Set(s); n.delete(key); return n; });
    try {
      const payload = { university_id: uni_id };
      if (degree_id) payload.degree_id = degree_id;
      await dropShortlist(payload);
      // Recompute unique-uni set from the authoritative /shortlist list
      // so we don't accidentally clear a uni that still has a sibling
      // course in the shortlist.
      try {
        const { data } = await api.get("/shortlist");
        const items = data.items || [];
        setShortlistUniIds(new Set(items.map((i) => i.university_id).filter(Boolean)));
      } catch {}
      toast(`${displayName} removed from your shortlist`);
    } catch {
      // rollback
      if (hadKey) setShortlistedIds((s) => new Set([...s, key]));
      toast.error("Could not remove. Please try again.");
    }
  };

  // Row actions: one shortlist request per row at a time (prevents double-click duplicates).
  const [shortlistBusy, setShortlistBusy] = useState(() => new Set());
  const toggleShortlistRow = async (r) => {
    const key = r.degree_id || r.university_id;
    if (shortlistBusy.has(key)) return;
    setShortlistBusy((s) => new Set(s).add(key));
    try {
      if (shortlistedIds.has(key)) await removeFromShortlistLocal(r.university_id, r.university_name, r.degree_id, r.degree_name);
      else await addToShortlist(r.university_id, r.university_name, r.degree_id, r.degree_name);
    } finally {
      setShortlistBusy((s) => { const n = new Set(s); n.delete(key); return n; });
    }
  };
  // Temporary comparison, scoped to the inputs the rows were computed from.
  const [compareRows, setCompareRows] = useCompareSelection(subjKey ? JSON.stringify(buildPayload()) : "");
  const compareIds = new Set(compareRows.map(compareKey));
  const toggleCompareRow = (r) => {
    const { list, error } = toggleCompare(compareRows, r);
    if (error === "max") { toast(`You can compare up to ${COMPARE_MAX} courses at once.`); return; }
    setCompareRows(list);
  };

  // "Your profile still lists X" drift detection. Returns:
  //   null  — nothing to show (anonymous, no server profile, or form
  //           still-empty pre-hydration).
  //   {}    — form perfectly matches the server snapshot ("all synced").
  //   { curriculum, primaryGrade, intendedSubject } — highlights the
  //           single most useful bit of drift so the hint stays terse.
  const profileDrift = (() => {
    if (!user || !serverGradeSnapshot) return null;
    const s = serverGradeSnapshot;
    const eq = (a, b) => {
      if (a === b) return true;
      if (a == null && (b === "" || b == null)) return true;
      if (b == null && (a === "" || a == null)) return true;
      // Loose numeric compare for grade fields (server stores 44.0, UI keeps 44).
      const na = Number(a), nb = Number(b);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
      return String(a).trim() === String(b).trim();
    };
    // Pick the "primary" grade field to surface — whichever matches the
    // current curriculum on the server. Falls back to any populated one.
    const CURRICULUM_PRIMARY = {
      a_level: "a_level", ib: "ib", cbse: "cbse", isc: "isc", atar: "atar",
      american_diploma: "american_diploma", gpa: "gpa",
      ap_composite: "ap_composite", abitur: "abitur",
      french_bac: "french_bac", hkdse: "hkdse", suneung: "suneung",
    };
    const primaryField = CURRICULUM_PRIMARY[s.curriculum] || null;
    const diffs = {};
    const ALL = [
      "curriculum",
      "a_level", "ib", "cbse", "american_diploma", "gpa", "atar", "isc",
      "ap_composite", "abitur", "french_bac", "hkdse", "suneung",
      "a_level_maths", "a_level_english", "ib_maths", "ib_english",
      "sat", "act", "ielts", "toefl", "activities_strength",
    ];
    for (const k of ALL) {
      if (!eq(form[k], s[k])) diffs[k] = s[k];
    }
    // Compare intended subject chip against server intended_degrees.
    const formSubj = (form.intended_degrees || []).map((x) => String(x).trim()).filter(Boolean);
    const serverSubj = (s.intended_degrees || []).map((x) => String(x).trim()).filter(Boolean);
    if (JSON.stringify(formSubj) !== JSON.stringify(serverSubj)) {
      diffs.intended_degrees = serverSubj;
    }
    if (Object.keys(diffs).length === 0) return {}; // fully synced

    // Compose a human-readable hint. Highlight (in priority order):
    //   1. Curriculum change     — "your profile lists IB"
    //   2. Primary grade change  — "your profile lists A*A*A" / "42"
    //   3. Intended-degree change — "your profile lists Politics"
    // We only surface ONE bit of drift so the hint stays a single line.
    const parts = [];
    if (diffs.curriculum !== undefined) {
      const label = ({
        a_level: "A Levels", ib: "IB", cbse: "CBSE", isc: "ISC",
        atar: "ATAR", american_diploma: "American Diploma",
        gpa: "GPA", ap_composite: "AP composite", abitur: "Abitur",
        french_bac: "Baccalauréat", hkdse: "HKDSE", suneung: "Suneung/CSAT",
      })[s.curriculum] || s.curriculum;
      parts.push(`curriculum: ${label}`);
    } else if (primaryField && diffs[primaryField] !== undefined && s[primaryField] != null) {
      parts.push(String(s[primaryField]));
    } else if (diffs.intended_degrees !== undefined && (s.intended_degrees || []).length) {
      parts.push((s.intended_degrees || []).slice(0, 2).join(", "));
    } else {
      // Something changed but not one of our headline fields — surface
      // a generic hint so the user still knows there's drift.
      parts.push("earlier grades");
    }
    return { hint: parts[0], diffCount: Object.keys(diffs).length };
  })();

  return (
    <div className={`max-w-7xl mx-auto px-6 lg:px-10 py-10 ${compareRows.length ? "pb-36 sm:pb-28" : ""}`}>
      <CompareTray rows={compareRows} onRemove={toggleCompareRow} onClear={() => setCompareRows([])} />
      {results.length > 0 && (
        <AhaRibbon
          storageKey="wua:aha:admission:v1"
          testId="aha-ribbon-admission"
          benefit={{
            title:    "Nice — your odds are calculated.",
            subtitle: "Save this profile once and get UCAS-deadline nudges + auto-comparisons on any device.",
          }}
          urgency={{
            title:    "Your grade profile is only on this device.",
            subtitle: "Sign in once — never re-type your grades, never miss a UCAS deadline.",
          }}
        />
      )}
      <SubjectsBadge className="mb-3" />
      <div className="label-uppercase">Chance calculator</div>
      <h1 className="font-editorial text-4xl sm:text-5xl mt-2">Where do I actually stand?</h1>
      <p className="mt-3 text-stone-600 max-w-2xl">
        Enter your profile once. We compare against each university's typical admitted student —
        not the published minimums — and classify each as Likely, Target, Ambitious or Highly Ambitious.
      </p>

      {hydratedFromProfile && (
        <div
          data-testid="calc-prefilled-from-profile"
          className="mt-6 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-900 flex items-center gap-2 max-w-2xl"
        >
          <Check className="h-3.5 w-3.5 text-emerald-700 shrink-0" />
          <span>
            Prefilled from your <Link to="/advanced" className="underline hover:no-underline">Advanced profile</Link>.
            Edit anything below — your saved profile stays as it is until you re-save.
          </span>
        </div>
      )}

      <div className="mt-10 grid lg:grid-cols-5 gap-8">
        <GradesForm
          form={form}
          update={update}
          submit={submit}
          loading={loading}
          hasResults={results.length > 0}
          signedIn={!!user}
          onSaveProfile={saveGradesToProfile}
          gradesConfirmed={explicitGrades.has(form.curriculum)}
          draftFromSaved={draftFromSaved}
          prefilledConfirmed={prefilledConfirmed.has(form.curriculum)}
          onConfirmGrades={() => {
            markExplicit(["curriculum", form.curriculum]);
            try { mirrorGradesToJourney(); } catch { /* best-effort */ }
            saveApplicantProfile(form, [...explicitRef.current]);
            if (user) saveGradesToProfile({ silent: true }).catch(() => {});
          }}
          savingProfile={savingProfileState}
          profileDrift={profileDrift}
          prefilledFromJourney={
            (journeyProfile?.what?.subjects_confirmed || []).length > 0 &&
            (form.intended_degrees || []).some((d) =>
              (journeyProfile?.what?.subjects_confirmed || [])
                .some((s) => (typeof s === "string" ? s : (s?.label || s?.slug)) === d)
            )
          }
        />

        <div className="lg:col-span-3">
          {results.length === 0 && !loading && !summary && (
            <>
              {excluded.length > 0 && intendedShown ? (
                <div>
                  <div data-testid="calc-empty-state-with-exclusions"
                       className="p-6 border border-dashed border-amber-400 bg-amber-50 rounded-lg text-stone-700 text-sm mb-4">
                    <div className="font-medium">No universities in our current database offer {intendedShown}.</div>
                    <div className="mt-1 text-stone-600">
                      See the {excluded.length} filtered {excluded.length === 1 ? "programme" : "programmes"} below,
                      or broaden your intended degrees above.
                    </div>
                  </div>
                  <ExcludedPanel
                    excluded={excluded}
                    intendedShown={intendedShown}
                    defaultOpen={true}
                    className="mt-4"
                  />
                </div>
              ) : (
                <div data-testid="calc-empty-state" className="p-10 border border-dashed border-stone-300 rounded-lg text-stone-500 text-sm">
                  Fill in your grades and intended subject on the left, then hit
                  <span className="font-underground-sc tracking-wider text-stone-700"> Classify me</span>.
                  After that, your buckets refresh automatically as you tweak your profile.
                </div>
              )}
            </>
          )}

          {/* Apply readiness — shared component, driven by the local
              shortlist tally so the counter reacts instantly to every
              add/remove without an extra round-trip. */}
          {summary && summary.total_matches > 0 && (
            <ApplyReadinessCard
              count={shortlistedIds.size}
              className="mt-4"
              testId="apply-readiness-card"
            />
          )}

          {/* City filter strip — hoisted above the summary block so it
              stays visible even when the summary returns 0 matches
              (so the user can widen or clear the filter). */}
          {summary && confirmedCityLabels.length > 0 && (
            <div
              className="mt-4 rounded-2xl border border-[#7C3AED]/20 bg-[#7C3AED]/5 px-4 py-3 flex flex-wrap items-center gap-2"
              data-testid="admission-city-filter"
            >
              <span className="text-[11px] uppercase tracking-wider text-[#7C3AED] font-medium mr-1">
                {cityFilter.size > 0
                  ? `Filtered to your cities · ${cityFilter.size}`
                  : "Cities you confirmed"}
              </span>
              {confirmedCityLabels.map((c) => {
                const active = cityFilter.has(c.slug);
                return (
                  <button
                    key={c.slug}
                    type="button"
                    data-testid={`admission-city-chip-${c.slug}`}
                    aria-pressed={active}
                    onClick={() => setCityFilter((prev) => {
                      const next = new Set(prev);
                      if (next.has(c.slug)) next.delete(c.slug);
                      else next.add(c.slug);
                      return next;
                    })}
                    className={[
                      "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium border transition-colors",
                      active
                        ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                        : "bg-white text-[#7C3AED] border-[#7C3AED]/40 hover:border-[#7C3AED]",
                    ].join(" ")}
                    title={active ? "Remove filter" : "Add filter"}
                  >
                    {c.name}
                    <span className={active ? "text-white/80" : "text-[#7C3AED]/60"}>
                      {active ? "×" : "+"}
                    </span>
                  </button>
                );
              })}
              {cityFilter.size > 0 && (
                <button
                  type="button"
                  onClick={() => setCityFilter(new Set())}
                  data-testid="admission-city-filter-clear"
                  className="ml-auto text-[11px] text-stone-600 hover:text-[#7C3AED] underline underline-offset-2"
                >
                  Show all cities
                </button>
              )}
            </div>
          )}

          {/* Empty state — the city filter is active but nothing in the
              chosen cities offers the picked subject. Clear affordance
              to widen the filter instead of a dead country grid. */}
          {summary && summary.total_matches === 0 && cityFilter.size > 0 && (
            <div
              className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/60 px-5 py-6 text-center"
              data-testid="admission-city-filter-empty"
            >
              <p className="text-sm text-amber-900 font-medium">
                No universities in {Array.from(cityFilter).length === 1 ? "this city" : "these cities"} offer
                {" "}
                <span className="font-semibold">
                  {((form.intended_degrees || [])[0] || "your subject")}
                </span>{" "}
                right now.
              </p>
              <p className="mt-1 text-xs text-amber-800/80">
                Widen the search by dropping a chip above, or clear the filter to see every country.
              </p>
              <button
                type="button"
                onClick={() => setCityFilter(new Set())}
                data-testid="admission-city-filter-clear-empty"
                className="mt-3 inline-flex items-center gap-1 rounded-full bg-[#7C3AED] px-4 py-2 text-xs font-medium text-white hover:bg-[#5b21b6]"
              >
                Show all cities
              </button>
            </div>
          )}

          {/* Country summary — progressive disclosure. Renders instantly
              once the summary endpoint returns; clicking a card fires the
              per-country drill-down for the detailed rows below. Expanding
              a country only reveals band chips; courses appear only when
              a band chip is clicked. */}
          {summary && summary.total_matches > 0 && (
            <div className="mt-4" data-testid="chance-country-summary">
              <CheapestCallout
                items={cheapest?.items || []}
                loading={cheapestLoading}
                intendedDegree={((form.intended_degrees || [])[0] || "").trim()}
              />

              {/* Confirm-chances panel — the explicit gate that turns
                  the PROVE chip green. Uses the intended-degrees from
                  the form as the "I've checked my chances on these"
                  list; student clicks confirm to lock them in. */}
              {(form.intended_degrees || []).filter(Boolean).length > 0 && (
                <div className="mt-6">
                  <SubjectChoicesPanel
                    subjects={(form.intended_degrees || []).filter(Boolean)}
                    cap={5}
                    signedIn={!!user}
                    onExplore={() => chancesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  />
                </div>
              )}
              <div ref={chancesRef} tabIndex={-1} data-testid="qc-chances-by-country" className="label-uppercase mb-3 mt-6 flex items-center justify-between gap-3 flex-wrap scroll-mt-24">
                <span>
                  Chances by country · {summary.total_matches} match{summary.total_matches === 1 ? "" : "es"} across {summary.total_countries} countr{summary.total_countries === 1 ? "y" : "ies"}
                </span>
                <button
                  type="button"
                  data-testid="quick-check-save-search"
                  onClick={() => {
                    if (!user) {
                      login();
                      return;
                    }
                    // Suggest a default name from the first intended
                    // subject so the input starts helpful, not blank.
                    const suggestion = (form.intended_degrees || [])[0] || "";
                    setSaveDialogName(suggestion);
                    setSaveDialogOpen(true);
                  }}
                  className="inline-flex items-center gap-1.5 text-[11px] font-underground-sc tracking-wider px-3 py-1.5 rounded-full border border-[#4C1D95] text-[#4C1D95] hover:bg-[#4C1D95] hover:text-white transition-colors"
                >
                  <Bookmark className="h-3 w-3" />
                  Save this search
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {summary.countries.map((c) => (
                  <RowErrorBoundary key={c.country} label={`quick-check-${c.country}`}>
                    <CountryCard
                      c={c}
                      summary={summary}
                      isOpen={!!expanded[c.country]}
                      isLoadingCountry={!!expanded[c.country] && !!countryData[c.country]?.loading}
                      loadError={!!countryData[c.country]?.error}
                      loadedResults={countryData[c.country] && !countryData[c.country].loading && !countryData[c.country].error ? countryData[c.country].results : undefined}
                      onRetry={loadCountry}
                      bandsOpen={openBands[c.country] || {}}
                      onExpand={expandCountry}
                      onToggleBand={toggleBand}
                      intendedSubject={((form.intended_degrees || [])[0] || "").trim()}
                    />
                  </RowErrorBoundary>
                ))}
              </div>
              <WithinReachPanel
                summary={summary}
                withinReach={withinReach}
                withinReachLoading={withinReachLoading}
                onLoad={loadWithinReach}
                intendedSubject={((form.intended_degrees || [])[0] || "").trim()}
              />
              <div className="mt-4">
                <ChanceLegend testId="calc-chance-legend" defaultOpen={false} />
              </div>
            </div>
          )}

          {loading && results.length === 0 && (
            <div data-testid="calc-loading-state" className="p-10 border border-dashed border-stone-300 rounded-lg text-stone-500 text-sm">
              Working out your chances…
            </div>
          )}

          {results.length === 0 && subjectFilter && (
            /* Filter yielded no rows in the currently-open bands — keep
               the pill visible so the family can switch back to All
               subjects without having to guess where the picker went. */
            <div className="mt-6 mb-3 flex items-center gap-3 flex-wrap" data-testid="subject-filter-empty-hint">
              <SubjectFilter
                options={(form.intended_degrees || []).filter(Boolean)}
                value={subjectFilter}
                onChange={(v) => { setSubjectFilter(v); setVisibleCount(PAGE_SIZE); }}
              />
              <span className="text-xs text-stone-600 font-mono">
                No courses match <span className="uppercase tracking-wider">{subjectFilter}</span> in the bands you've opened. Try opening another band or switching back to All subjects.
              </span>
            </div>
          )}

          {results.length > 0 && (
            <div ref={resultsRef}>
              <div className="label-uppercase mt-6 mb-3 flex items-center justify-between flex-wrap gap-2">
                <span>
                  Your buckets
                  <span className="ml-2 text-stone-400 font-mono normal-case tracking-normal">
                    {(() => {
                      if (results.length === 1) return "1 course";
                      if (!groupByUni) {
                        return `${Math.min(visibleCount, results.length)} of ${results.length} courses`;
                      }
                      const groupCount = groupByUniversity(results).length;
                      return `${Math.min(visibleCount, groupCount)} of ${groupCount} universities · ${results.length} courses`;
                    })()}
                  </span>
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Subject filter — surfaces only when the family picked
                      more than one intended subject in the wizard. Narrows
                      both `results` and `excluded` via each row's backend-
                      attached `matched_intended` tag. */}
                  <SubjectFilter
                    options={(form.intended_degrees || []).filter(Boolean)}
                    value={subjectFilter}
                    onChange={(v) => {
                      setSubjectFilter(v);
                      setVisibleCount(PAGE_SIZE);
                    }}
                  />
                  <label htmlFor="buckets-sort" className="text-[10px] font-underground-sc tracking-wider text-stone-500">
                    Sort by
                  </label>
                  <select
                    id="buckets-sort"
                    data-testid="buckets-sort-dropdown"
                    value={bucketsSort}
                    onChange={(e) => {
                      setBucketsSort(e.target.value);
                      setVisibleCount(PAGE_SIZE);
                    }}
                    className="text-[11px] font-underground-sc tracking-wider bg-white border border-stone-300 rounded-md px-2 py-1 hover:border-[#4C1D95] focus:outline-none focus:border-[#4C1D95] cursor-pointer"
                  >
                    <option value="competitive">Most competitive first</option>
                    <option value="cheapest">Cheapest first</option>
                    <option value="chance">Safest bets first</option>
                    <option value="value">Best value (chance ÷ cost)</option>
                    <option value="salary">Highest graduate salary</option>
                    <option value="acceptance">Highest acceptance rate</option>
                    <option value="alpha">A → Z alphabetical</option>
                  </select>
                  {/* Group-by-university toggle — collapses each uni
                      into a flagship row + a "N variations ▾" chip
                      the applicant can click to expand siblings. */}
                  <button
                    type="button"
                    data-testid="buckets-group-by-uni-toggle"
                    onClick={() => setGroupByUni((v) => !v)}
                    aria-pressed={groupByUni}
                    className={
                      "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] font-underground-sc tracking-wider transition-colors " +
                      (groupByUni
                        ? "border-[#4C1D95] bg-[#4C1D95] text-white hover:bg-[#3a1476]"
                        : "border-stone-300 bg-white text-stone-700 hover:border-[#4C1D95]")
                    }
                    title="Collapse each university into one flagship course; click a chip to reveal sibling courses at the same uni."
                  >
                    Group by uni
                  </button>
                </div>
              </div>
              {bucketsSort === "competitive" && (
                /* Discoverability nudge — Oxbridge / LSE / Imperial etc
                   almost always classify as Ambitious or Long-shot for
                   a typical applicant, so a family who only opened
                   Likely / Target would miss the "most competitive"
                   courses they came for. Non-sticky, easy to ignore. */
                <div
                  data-testid="buckets-sort-competitive-band-nudge"
                  className="mb-3 text-[11px] text-stone-500 leading-snug"
                >
                  Top-competitive courses usually classify as{" "}
                  <span className="font-underground-sc tracking-wider text-stone-600">Ambitious</span>{" "}
                  or{" "}
                  <span className="font-underground-sc tracking-wider text-stone-600">Long-shot</span>
                  {" "}— open those bands under each country to reveal them.
                </div>
              )}
              {bucketsSort === "competitive" && <CompetitiveReadingNote results={results} />}
              {(() => {
                // Coverage warnings — honesty when the picked sort
                // depends on a field that isn't populated for most
                // rows. Without this, "Highest graduate salary" would
                // silently rank the handful of unis where we DO have
                // LEO/Scorecard data at the top, misleading a family
                // into thinking Abertay actually beats LSE for
                // Business. Explicit is kinder than clever.
                const needsSalary = (bucketsSort === "salary");
                const needsAcceptance = (bucketsSort === "acceptance");
                const needsChance = (bucketsSort === "chance");
                const needsCompetitive = (bucketsSort === "competitive");
                if (!needsSalary && !needsAcceptance && !needsChance && !needsCompetitive) return null;
                let key = null, label = "";
                let missing = 0;
                if (needsSalary) {
                  key = "median_earnings_1yr_usd";
                  label = "1-year graduate salary";
                  missing = results.filter((r) => r[key] == null).length;
                } else if (needsAcceptance) {
                  key = "acceptance_rate";
                  label = "acceptance rate";
                  missing = results.filter((r) => r[key] == null).length;
                } else if (needsCompetitive) {
                  // Rows with no HESA tariff AND no acceptance rate
                  // fall to the bottom — mirror salary/acceptance UX.
                  // Also render a persistent one-liner (even at 0%
                  // missing) that names the exact signals used, so
                  // the family can never mistake this for a Guardian /
                  // CUG league position.
                  label = "entry-tariff or acceptance-rate signal";
                  missing = results.filter((r) => r.competitiveness_index == null).length;
                  // Break down the signal mix (subject-precise vs
                  // uni-average) so the banner can name it honestly.
                  const subjectCount = results.filter((r) => r.subject_offer_rate != null).length;
                  const uniOnlyCount = results.filter(
                    (r) => r.competitiveness_index != null && r.subject_offer_rate == null
                  ).length;
                  if (missing === 0) {
                    return (
                      <div
                        data-testid="buckets-sort-competitive-hint"
                        className="mb-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-[12px] text-stone-700 leading-snug"
                      >
                        <span className="font-mono font-semibold text-stone-800">
                          How this sort works:
                        </span>{" "}
                        Sorted by entry competitiveness — UK courses
                        blend HESA typical UCAS tariff (40%) with
                        1 − acceptance rate (60%); others use
                        acceptance rate alone. Not a league-table
                        ranking.
                        {subjectCount > 0 && (
                          <> <span className="text-indigo-800 font-mono">{subjectCount} of {results.length} courses use subject-precise UCAS offer rates</span> (the more reliable signal); the rest use uni-average acceptance.</>
                        )}
                        {subjectCount === 0 && uniOnlyCount > 0 && (
                          <> All rows use uni-average acceptance rates — course-level UCAS End of Cycle data hasn't been ingested yet.</>
                        )}
                      </div>
                    );
                  }
                } else {
                  // Chance sort — "missing" = rows where the calculator
                  // fell back to gap_score = 0.0 (no published entry
                  // requirement). Those sink below rows with a real signal.
                  label = "chance-fit signal (published entry requirements)";
                  missing = results.filter((r) => (r?.classification?.gap_score ?? 0) === 0).length;
                  // For "Safest bets first" we always want a soft hint —
                  // the sort is technically a "least-selective first"
                  // list which surprises users expecting brand-name unis.
                  if (missing === 0) {
                    return (
                      <div
                        data-testid="buckets-sort-chance-hint"
                        className="mb-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-[12px] text-stone-700 leading-snug"
                      >
                        <span className="font-mono font-semibold text-stone-800">
                          Reading this list:
                        </span>{" "}
                        "Safest bets" orders results by a rule-based mix of
                        your grade match and each university's published
                        acceptance rate. It is not a prediction of your
                        chance, and not the same as "best university". Less-selective
                        unis with higher acceptance rates rank first
                        here by design. Combine with{" "}
                        <span className="italic">Highest graduate salary</span>{" "}
                        or the Advanced Search 5-layer match if you're
                        looking for "safe + strong".
                      </div>
                    );
                  }
                }
                if (missing === 0) return null;
                const pctMissing = Math.round((missing / results.length) * 100);
                // 100%-missing degeneracy — sort is meaningless. Say so
                // explicitly and nudge the user back to a defensible sort.
                if (missing === results.length) {
                  return (
                    <div
                      data-testid="buckets-sort-coverage-degenerate"
                      className="mb-3 rounded-md border border-red-300 bg-red-50/70 px-3 py-2 text-[12px] text-stone-800 leading-snug flex flex-wrap items-center justify-between gap-2"
                    >
                      <span>
                        <span className="font-mono font-semibold text-red-900">
                          No sourced {label} data
                        </span>{" "}
                        for any of these {results.length} courses yet — this sort would
                        rank courses alphabetically, not by earnings, so we'd be misleading
                        you. Reverting to Cheapest is the honest fallback.
                      </span>
                      <button
                        type="button"
                        data-testid="buckets-sort-fallback"
                        onClick={() => setBucketsSort("cheapest")}
                        className="rounded-full border border-red-400 bg-white px-3 py-1 text-[11px] font-underground-sc tracking-wider text-red-800 hover:bg-red-50"
                      >
                        Switch to Cheapest
                      </button>
                    </div>
                  );
                }
                return (
                  <div
                    data-testid="buckets-sort-coverage-warning"
                    className="mb-3 rounded-md border border-amber-300 bg-amber-50/70 px-3 py-2 text-[12px] text-stone-800 leading-snug"
                  >
                    <span className="font-mono font-semibold text-amber-900">
                      Sparse data:
                    </span>{" "}
                    {missing} of {results.length} courses ({pctMissing}%) don't publish a{" "}
                    {label} we can source. Those courses sink to the bottom of this sort —
                    the top of the list is limited to universities where we DO have the
                    field, which can hide well-known unis that simply haven't yet been
                    hydrated. Cross-check with{" "}
                    <a href="/methodology" className="underline text-[#4C1D95]">the methodology page</a>{" "}
                    or switch to "Cheapest first" for full coverage.
                  </div>
                );
              })()}
              <div className="space-y-3">
                {(() => {
                  // Compute cost + salary + chance signals once so the
                  // sort switch is O(N) per re-render, not O(N × F).
                  const enriched = results.map((r) => {
                    const c = r.true_cost || {};
                    const known =
                      (c.tuition_usd || 0) +
                      (c.fees_usd || 0) +
                      (c.accommodation_usd || 0) +
                      (c.food_usd || 0);
                    // Unknown tuition → no total, so cost sorts never rank it as cheap.
                    const total = c.tuition_usd == null || c.complete === false ? null : known;
                    // gap_score is [-1, +1] where higher = more likely.
                    // Normalise to [0, 1] for the "best value" formula.
                    const gap = r?.classification?.gap_score ?? 0;
                    const chanceNorm = Math.max(0, Math.min(1, (gap + 1) / 2));
                    return { r, total, known, chanceNorm, gap };
                  });
                  const scaleMax = Math.max(0, ...enriched.map((x) => x.known)) || 1;
                  // Nulls (missing salary / acceptance) go to the BOTTOM
                  // of that sort mode — sort semantics stay honest.
                  const bottomOf = (v) => (v == null || Number.isNaN(v));
                  let scored;
                  switch (bucketsSort) {
                    case "chance":
                      // Highest displayed estimated chance of a place first (see lib/safestSort).
                      scored = [...enriched].sort((a, b) => compareSafest(a.r, b.r));
                      break;
                    case "value":
                      // (normalised chance) ÷ (total cost, USD) — higher = better.
                      // Multiply by 1e5 so the sort key is a legible number,
                      // then descending.
                      scored = [...enriched].sort((a, b) =>
                        (b.total == null ? -1 : b.chanceNorm / (b.total || 1)) - (a.total == null ? -1 : a.chanceNorm / (a.total || 1))
                      );
                      break;
                    case "salary":
                      scored = [...enriched].sort((a, b) => {
                        const av = a.r.median_earnings_1yr_usd, bv = b.r.median_earnings_1yr_usd;
                        if (bottomOf(av) && bottomOf(bv)) return 0;
                        if (bottomOf(av)) return 1;
                        if (bottomOf(bv)) return -1;
                        return bv - av;
                      });
                      break;
                    case "acceptance":
                      scored = [...enriched].sort((a, b) => {
                        const av = a.r.acceptance_rate, bv = b.r.acceptance_rate;
                        if (bottomOf(av) && bottomOf(bv)) return 0;
                        if (bottomOf(av)) return 1;
                        if (bottomOf(bv)) return -1;
                        return bv - av;
                      });
                      break;
                    case "competitive":
                      // Entry-competitiveness stand-in for a proper
                      // subject-league position: HESA typical tariff
                      // for UK, `1 - acceptance_rate` elsewhere.
                      // Rows without either signal
                      // (`competitiveness_source == null`) sink to
                      // the bottom — the sparse-data hint above the
                      // list explains why. The Sort dropdown's
                      // tooltip repeats the disclaimer.
                      scored = [...enriched].sort((a, b) => compareCompetitive(a.r, b.r));
                      break;
                    case "alpha":
                      scored = [...enriched].sort((a, b) =>
                        (a.r.university_name || "").localeCompare(b.r.university_name || "")
                      );
                      break;
                    case "cheapest":
                    default:
                      scored = [...enriched].sort((a, b) => (a.total ?? Infinity) - (b.total ?? Infinity));
                  }
                  // Group-by-university transformation. Collapses each
                  // uni into a flagship row (highest by current sort)
                  // + a "N variations" chip that expands siblings on
                  // click. Preserves the sort's intent because the
                  // first occurrence of each uni_id in `scored` IS the
                  // top-ranked variation.
                  const rows = scored.map(({ r }) => r);
                  const groups = groupByUni
                    ? groupByUniversity(rows)
                    : rows.map((r) => ({
                        university_id: r.university_id,
                        flagship: r,
                        variations: [],
                        count: 1,
                      }));
                  const sliceGroups = groups.slice(0, visibleCount);
                  const tiedIds = bucketsSort === "competitive"
                    ? sameScoreIds(groupedDisplayOrder(groups, (uid) => uid && expandedUniGroups.has(uid)))
                    : new Set();
                  const renderRow = (r, opts = {}) => {
                    const rowId = r.degree_id || r.university_id;
                    return (
                      <div
                        key={rowId}
                        className={opts.indented ? "pl-6 border-l-2 border-stone-200 ml-2" : ""}
                      >
                        <ResultRow
                          r={r}
                          narrative={aiByUni[rowId]}
                          isLoading={aiLoadingId === rowId}
                          onExplain={getNarrative}
                          shortlistedIds={shortlistedIds}
                          onAddToShortlist={addToShortlist}
                          onRemoveFromShortlist={removeFromShortlistLocal}
                          onFixGap={setGapFixer}
                          user={user}
                          onSignIn={login}
                          costScaleMax={scaleMax}
                          highlightSalary={bucketsSort === "salary" || bucketsSort === "value"}
                          highlightAcceptance={bucketsSort === "acceptance"}
                          highlightCompetitive={bucketsSort === "competitive"}
                          sameScore={tiedIds.has(rowId)}
                          shortlistBusy={shortlistBusy.has(rowId)}
                          onToggleShortlist={() => toggleShortlistRow(r)}
                          compared={compareIds.has(r.degree_id)}
                          compareFull={compareRows.length >= COMPARE_MAX}
                          onToggleCompare={() => toggleCompareRow(r)}
                        />
                      </div>
                    );
                  };
                  return sliceGroups.flatMap((g) => {
                    const uid = g.university_id;
                    const isExpanded = uid && expandedUniGroups.has(uid);
                    const nodes = [renderRow(g.flagship)];
                    if (g.variations.length > 0) {
                      nodes.push(
                        <div
                          key={`${uid}-chip`}
                          className="pl-6 ml-2 -mt-2 mb-2"
                        >
                          <button
                            type="button"
                            data-testid={`variations-toggle-${uid}`}
                            onClick={() =>
                              setExpandedUniGroups((prev) => {
                                const next = new Set(prev);
                                if (next.has(uid)) next.delete(uid);
                                else next.add(uid);
                                return next;
                              })
                            }
                            aria-expanded={isExpanded}
                            className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-white px-2.5 py-0.5 text-[10px] font-underground-sc tracking-wider text-stone-600 hover:border-[#4C1D95] hover:text-[#4C1D95] transition-colors"
                          >
                            {g.variations.length} variation{g.variations.length === 1 ? "" : "s"}
                            <span className="ml-0.5">{isExpanded ? "▴" : "▾"}</span>
                          </button>
                        </div>
                      );
                    }
                    if (isExpanded) {
                      g.variations.forEach((child) => nodes.push(renderRow(child, { indented: true })));
                    }
                    return nodes;
                  });
                })()}
              </div>

              {/* Client-side pagination. Rendering 800+ rows at once
                  freezes the browser; we ask families for one click
                  per additional 25 courses. Counts groups when the
                  Group-by-uni toggle is on so a family with 200 LSE
                  variants isn't stuck paginating through LSE. */}
              {(() => {
                const rows = results;
                const groupCount = groupByUni
                  ? groupByUniversity(rows).length
                  : rows.length;
                if (groupCount <= visibleCount) return null;
                return (
                  <div className="mt-4 flex flex-col items-center gap-2">
                    <button
                      type="button"
                      data-testid="calc-show-more"
                      onClick={() =>
                        setVisibleCount((n) => Math.min(n + PAGE_SIZE, groupCount))
                      }
                      className="px-5 py-2.5 rounded-md border border-[#4C1D95] text-[#4C1D95] hover:bg-[#4C1D95] hover:text-white transition-colors text-sm font-underground-sc tracking-wider"
                    >
                      Show {Math.min(PAGE_SIZE, groupCount - visibleCount)} more →
                    </button>
                    <div className="text-[11px] text-stone-500 font-mono">
                      {groupCount - visibleCount} more {groupByUni ? "universit" + (groupCount - visibleCount === 1 ? "y" : "ies") : "course" + (groupCount - visibleCount === 1 ? "" : "s")} in this band
                    </div>
                  </div>
                );
              })()}
              {(() => {
                const rows = results;
                const groupCount = groupByUni
                  ? groupByUniversity(rows).length
                  : rows.length;
                if (!(groupCount > PAGE_SIZE && visibleCount >= groupCount)) return null;
                return (
                  <div className="mt-4 text-center text-[11px] text-stone-500 font-mono">
                    You've reached the end of this band ✓
                  </div>
                );
              })()}

              {excluded.length > 0 && intendedShown && (
              <ExcludedPanel
                excluded={excluded}
                intendedShown={intendedShown}
                className="mt-8"
              />
              )}
            </div>
          )}

          {results.length > 0 && (
            <ProfileNudge surface="quick_check" when={results.length > 0} className="mt-8"
              priorities={["budget", "fee_status", "goals", "preferences"]}
              lead="These results use your grades and subjects." />
          )}
          {results.length > 0 && (
            <UpsellToAdvanced user={user} onSignIn={login} intendedDegree={form.intended_degrees.join(", ")} />
          )}
        </div>
      </div>

      {gapFixer && (
        <GapFixerModal
          open={!!gapFixer}
          onOpenChange={(v) => { if (!v) setGapFixer(null); }}
          gapKey={gapFixer.gapKey}
          gapValue={gapFixer.gapValue}
          degreeId={gapFixer.degreeId}
          degreeName={gapFixer.degreeName}
          universityName={gapFixer.universityName}
        />
      )}

      {/* Save-search dialog — replaces the browser's native prompt. */}
      <Dialog open={saveDialogOpen} onOpenChange={(v) => {
        if (!savingSearch) setSaveDialogOpen(v);
      }}>
        <DialogContent
          className="sm:max-w-md"
          data-testid="save-search-dialog"
        >
          <DialogHeader>
            <DialogTitle>Save this search</DialogTitle>
            <DialogDescription>
              Give it a name so you can come back later and see how your
              chances have moved.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <label htmlFor="save-search-name" className="text-[11px] uppercase tracking-wider text-stone-500 font-medium">
              Search name
            </label>
            <Input
              id="save-search-name"
              autoFocus
              value={saveDialogName}
              onChange={(e) => setSaveDialogName(e.target.value)}
              placeholder="e.g. Maths, £30k budget"
              className="mt-1.5"
              data-testid="save-search-input"
              onKeyDown={(e) => {
                if (e.key === "Enter" && saveDialogName.trim() && !savingSearch) {
                  e.preventDefault();
                  document.querySelector("[data-testid='save-search-confirm']")?.click();
                }
              }}
              maxLength={80}
            />
            <p className="mt-2 text-xs text-stone-500">
              Suggestion: use the subject + budget so it's easy to spot in your list.
            </p>
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setSaveDialogOpen(false)}
              disabled={savingSearch}
              data-testid="save-search-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              data-testid="save-search-confirm"
              disabled={!saveDialogName.trim() || savingSearch}
              onClick={async () => {
                setSavingSearch(true);
                try {
                  await api.post("/saved-searches", {
                    name: saveDialogName.trim(),
                    payload: buildPayload(),
                    kind: "quick_check",
                  });
                  toast.success("Search saved to your account.", {
                    description: "Open Saved searches to refresh it later and see how your chances have moved.",
                    action: {
                      label: "View saved",
                      onClick: () => navigate("/saved"),
                    },
                  });
                  setSaveDialogOpen(false);
                  setSaveDialogName("");
                } catch {
                  toast.error("Could not save. Please try again.");
                } finally {
                  setSavingSearch(false);
                }
              }}
              className="bg-[#4C1D95] hover:bg-[#3B0764] text-white"
            >
              {savingSearch ? "Saving…" : "Save search"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


